'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { toast } from 'sonner';
import type { GI } from './GIContent';

interface GITableProps {
  gis: GI[];
  onViewDetail: (gi: GI) => void;
}

export default function GITable({ gis, onViewDetail }: GITableProps) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [approveTarget, setApproveTarget] = useState<GI | null>(null);
  const [issueTarget, setIssueTarget] = useState<GI | null>(null);
  const [recipient, setRecipient] = useState('');

  if (gis.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-14 text-center shadow-card">
        <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center mx-auto mb-4">
          <Icon name="TruckIcon" size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-sm font-600 text-foreground">No issuances found</h3>
        <p className="text-xs text-muted-foreground mt-1.5">
          No Goods Issuances match your current filter. Create a new GI request to get started.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card border border-border rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="w-8 px-3 py-3" />
                {['GI No.', 'Requested By', 'Site', 'BOM / Project', 'Items', 'Total Value', 'Status', 'Issued Date', 'Recipient', 'Actions'].map((h) => (
                  <th key={`gi-th-${h}`} className="text-left text-[10px] font-600 uppercase tracking-wider text-muted-foreground px-3 py-3 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gis.map((gi, idx) => (
                <React.Fragment key={gi.id}>
                  <tr
                    onMouseEnter={() => setHoveredRow(gi.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    className={`border-b border-border transition-colors ${idx % 2 === 1 ? 'bg-muted/20' : ''} ${hoveredRow === gi.id ? 'bg-teal/5' : ''}`}
                  >
                    <td className="px-3 py-3 text-center">
                      <button
                        onClick={() => setExpandedRow(expandedRow === gi.id ? null : gi.id)}
                        className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground"
                      >
                        <Icon name={expandedRow === gi.id ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={13} />
                      </button>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-mono-data text-xs font-600 text-foreground">{gi.giNumber}</span>
                    </td>
                    <td className="px-3 py-3 text-xs text-foreground">{gi.requestedBy}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{gi.site}</td>
                    <td className="px-3 py-3">
                      {gi.bomCode ? (
                        <div>
                          <span className="text-xs font-500 text-secondary">{gi.bomCode}</span>
                          <p className="text-[11px] text-muted-foreground truncate max-w-[140px]">{gi.bomProject}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs font-500 text-foreground">{gi.lineItems.length} line{gi.lineItems.length !== 1 ? 's' : ''}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-sm font-600 tabular-nums text-foreground">
                        € {gi.totalValue.toLocaleString('de-DE', { minimumFractionDigits: 0 })}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={gi.status as Parameters<typeof StatusBadge>[0]['status']} size="sm" />
                        {gi.status === 'APPROVED' && (
                          <span className="text-[9px] bg-accent text-accent-foreground px-1.5 py-0.5 rounded font-700 animate-pulse">ISSUE NOW</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {gi.issuedDate ?? '—'}
                    </td>
                    <td className="px-3 py-3">
                      {gi.recipient ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-foreground">{gi.recipient}</span>
                          {gi.signatureCaptured && (
                            <Icon name="PencilSquareIcon" size={11} className="text-teal" title="Digital signature captured" />
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className={`flex items-center gap-1 transition-opacity ${hoveredRow === gi.id ? 'opacity-100' : 'opacity-0'}`}>
                        <button
                          title="View detail"
                          onClick={() => onViewDetail(gi)}
                          className="p-1.5 rounded-md hover:bg-secondary/10 text-secondary transition-colors"
                        >
                          <Icon name="EyeIcon" size={14} />
                        </button>
                        {gi.status === 'PENDING_APPROVAL' && (
                          <button
                            title="Approve GI request"
                            onClick={() => setApproveTarget(gi)}
                            className="p-1.5 rounded-md hover:bg-teal/10 text-teal transition-colors"
                          >
                            <Icon name="CheckCircleIcon" size={14} />
                          </button>
                        )}
                        {gi.status === 'APPROVED' && (
                          <button
                            title="Record physical issuance"
                            onClick={() => setIssueTarget(gi)}
                            className="p-1.5 rounded-md hover:bg-accent/10 text-accent-foreground bg-accent/20 transition-colors"
                          >
                            <Icon name="TruckIcon" size={14} />
                          </button>
                        )}
                        {gi.status === 'ISSUED' && (
                          <button
                            title="Print gate pass"
                            onClick={() => toast.info(`Generating gate pass for ${gi.giNumber}…`)}
                            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground transition-colors"
                          >
                            <Icon name="PrinterIcon" size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded row */}
                  {expandedRow === gi.id && (
                    <tr key={`${gi.id}-exp`} className="border-b border-border">
                      <td colSpan={11} className="px-6 py-4 bg-muted/30">
                        <div className="rounded-lg border border-border overflow-hidden bg-card">
                          <div className="px-4 py-2 bg-muted/50 border-b border-border flex items-center gap-2">
                            <Icon name="ListBulletIcon" size={14} className="text-muted-foreground" />
                            <span className="text-xs font-600 text-muted-foreground uppercase tracking-wider">Line Items — {gi.giNumber}</span>
                          </div>
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-border">
                                {['Item Code', 'Item Name', 'Requested', 'Issued', 'Available', 'UOM', 'Unit Cost', 'Line Total', 'Availability'].map((h) => (
                                  <th key={`gi-li-th-${h}`} className="text-left text-[10px] font-600 uppercase tracking-wider text-muted-foreground px-3 py-2">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {gi.lineItems.map((li) => {
                                const canFulfill = li.availableQty >= li.requestedQty;
                                return (
                                  <tr key={li.id} className="hover:bg-muted/30 transition-colors">
                                    <td className="px-3 py-2">
                                      <span className="font-mono-data text-xs text-foreground">{li.itemCode}</span>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-foreground">{li.itemName}</td>
                                    <td className="px-3 py-2 text-xs tabular-nums text-foreground">{li.requestedQty}</td>
                                    <td className="px-3 py-2 text-xs tabular-nums text-teal font-500">{li.issuedQty > 0 ? li.issuedQty : '—'}</td>
                                    <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">{li.availableQty}</td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">{li.uom}</td>
                                    <td className="px-3 py-2 text-xs tabular-nums text-foreground">€ {li.unitCost.toFixed(2)}</td>
                                    <td className="px-3 py-2 text-xs tabular-nums font-600 text-foreground">€ {li.totalCost.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                                    <td className="px-3 py-2">
                                      {canFulfill ? (
                                        <span className="text-[10px] bg-teal/10 text-teal px-2 py-0.5 rounded-full font-500 border border-teal/20">
                                          Sufficient
                                        </span>
                                      ) : (
                                        <span className="text-[10px] bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-500 border border-red-200">
                                          Shortage: {li.requestedQty - li.availableQty} {li.uom}
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                          <span><span className="font-600 text-foreground">Purpose:</span> {gi.purpose}</span>
                          {gi.bomCode && (
                            <span className="flex items-center gap-1">
                              <Icon name="ClipboardDocumentListIcon" size={12} />
                              <span className="font-600 text-secondary">{gi.bomCode}</span> — BOM consumption will be updated on issuance
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-border">
          <span className="text-xs text-muted-foreground">{gis.length} issuance{gis.length !== 1 ? 's' : ''} shown</span>
        </div>
      </div>

      {/* Approve confirm */}
      <ConfirmModal
        open={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        onConfirm={() => {
          toast.success(`${approveTarget?.giNumber} approved. Ready for physical issuance.`);
          setApproveTarget(null);
        }}
        title="Approve GI Request"
        message={`Approve ${approveTarget?.giNumber} requested by ${approveTarget?.requestedBy}? Stock will be reserved and the request will move to physical issuance stage.`}
        confirmLabel="Approve Request"
        variant="warning"
      />

      {/* Physical issuance modal */}
      {issueTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-primary/40 backdrop-blur-sm" onClick={() => setIssueTarget(null)} />
          <div className="relative bg-card rounded-xl shadow-modal w-full max-w-lg fade-in p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-600 text-foreground">Record Physical Issuance</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{issueTarget.giNumber} · {issueTarget.site}</p>
              </div>
              <button onClick={() => setIssueTarget(null)} className="p-1.5 hover:bg-muted rounded-md transition-colors">
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Items summary */}
              <div className="bg-muted/40 rounded-lg px-4 py-3">
                <p className="text-xs font-600 text-muted-foreground mb-2 uppercase tracking-wider">Items to Issue</p>
                {issueTarget.lineItems.map((li) => (
                  <div key={li.id} className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
                    <span className="text-xs text-foreground">{li.itemName}</span>
                    <span className="text-xs font-600 tabular-nums text-foreground">{li.requestedQty} {li.uom}</span>
                  </div>
                ))}
              </div>

              {/* Recipient */}
              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">
                  Recipient Name <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Person physically receiving the goods at the site gate</p>
                <input
                  type="text"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="Full name of recipient…"
                  className="w-full px-3 py-2.5 border border-border rounded-lg text-sm bg-input text-foreground outline-none focus:ring-2 focus:ring-ring/30 transition-all"
                />
              </div>

              {/* Signature stub */}
              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Digital Signature</label>
                <p className="text-xs text-muted-foreground mb-2">Recipient must sign below to confirm physical receipt</p>
                <div className="border-2 border-dashed border-border rounded-lg h-24 flex flex-col items-center justify-center bg-muted/20 cursor-pointer hover:bg-muted/40 transition-colors">
                  <Icon name="PencilSquareIcon" size={20} className="text-muted-foreground mb-1" />
                  <p className="text-xs text-muted-foreground">Tap to sign (signature pad)</p>
                  <p className="text-[10px] text-muted-foreground/60 mt-0.5">PWA offline-capable via IndexedDB sync</p>
                </div>
              </div>

              {/* BOM impact */}
              {issueTarget.bomCode && (
                <div className="bg-secondary/5 border border-secondary/20 rounded-lg px-4 py-3">
                  <p className="text-xs font-600 text-secondary mb-1 flex items-center gap-1.5">
                    <Icon name="ClipboardDocumentListIcon" size={13} />
                    BOM Consumption Impact
                  </p>
                  <p className="text-xs text-foreground">
                    Issuing these items will update <span className="font-600">{issueTarget.bomCode}</span> — {issueTarget.bomProject}.
                    Consumption tracking will be updated automatically.
                  </p>
                </div>
              )}

              <div className="flex gap-3 justify-end pt-2">
                <button onClick={() => setIssueTarget(null)} className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors">
                  Cancel
                </button>
                <button
                  disabled={!recipient.trim()}
                  onClick={() => {
                    toast.success(`${issueTarget.giNumber} physically issued to ${recipient}. Gate pass generated.`);
                    setIssueTarget(null);
                    setRecipient('');
                  }}
                  className="flex items-center gap-2 px-5 py-2 bg-teal text-teal-foreground rounded-lg text-sm font-600 hover:bg-teal/90 transition-all active:scale-95 disabled:opacity-50"
                >
                  <Icon name="TruckIcon" size={15} />
                  Confirm Issuance & Print Gate Pass
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}